import React, { useState, useRef, useEffect, useCallback } from 'react';

export default function SearchBar({ onNavigate, apiBase = '' }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const listboxId = 'search-suggestions-listbox';

  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('scenario_os_token')
        || localStorage.getItem('token')
        || '';
      const res = await fetch(
        `${apiBase}/scenario-os/curriculum/search/suggestions?q=${encodeURIComponent(q)}&limit=5`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        setSuggestions([]);
        return;
      }
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchSuggestions]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setShowDropdown(false);
      setActiveIndex(-1);
      if (onNavigate) onNavigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const s = suggestions[activeIndex];
      setQuery(s.title);
      setShowDropdown(false);
      setActiveIndex(-1);
      if (onNavigate) onNavigate(`/search?q=${encodeURIComponent(s.title)}`);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  const dropdownVisible = showDropdown && suggestions.length > 0;
  const activeDescendant = activeIndex >= 0 ? `search-option-${activeIndex}` : undefined;

  return (
    <form onSubmit={handleSubmit} style={{ position: 'relative', width: '100%', maxWidth: 360 }}>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search courses, lessons..."
        value={query}
        onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); setActiveIndex(-1); }}
        onFocus={() => query.length >= 2 && setShowDropdown(true)}
        onBlur={() => setTimeout(() => { setShowDropdown(false); setActiveIndex(-1); }, 200)}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={dropdownVisible}
        aria-controls={listboxId}
        aria-activedescendant={activeDescendant}
        aria-autocomplete="list"
        aria-label="Search courses, lessons"
        style={{
          width: '100%',
          padding: '7px 12px 7px 32px',
          borderRadius: 8,
          border: '1px solid var(--border-color, #d1d5db)',
          fontSize: '0.85rem',
          background: 'var(--surface-secondary, #f9fafb)',
          outline: 'none',
        }}
      />
      <span
        style={{
          position: 'absolute', left: 10, top: '50%',
          transform: 'translateY(-50%)', fontSize: '0.85rem', color: '#9ca3af',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      >
        &#x1F50D;
      </span>

      {dropdownVisible && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Search suggestions"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            background: 'var(--surface-primary, #fff)',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 100, overflow: 'hidden',
          }}
        >
          {suggestions.map((s, i) => (
            <div
              key={`${s.type}-${s.id}-${i}`}
              id={`search-option-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={() => {
                setQuery(s.title);
                setShowDropdown(false);
                setActiveIndex(-1);
                if (onNavigate) onNavigate(`/search?q=${encodeURIComponent(s.title)}`);
              }}
              onMouseEnter={() => setActiveIndex(i)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem',
                borderBottom: i < suggestions.length - 1
                  ? '1px solid var(--border-color, #f3f4f6)' : 'none',
                background: i === activeIndex ? 'var(--surface-secondary, #f9fafb)' : 'transparent',
              }}
            >
              <div style={{ fontWeight: 500 }}>{s.title}</div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                {s.type} &middot; {s.domain}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <span style={{
          position: 'absolute', right: 10, top: '50%',
          transform: 'translateY(-50%)', fontSize: '0.7rem', color: '#9ca3af',
        }}>
          ...
        </span>
      )}
    </form>
  );
}
